import { useCallback, useState } from "react";
import { IAddress, IUserAddress } from "@interface/customerDetails/IAddress";
import { useFetchState } from "@hooks/useFetchState";
import { getProspectReference } from "@utils/storage";
import { IEirSalesSavePrequalPayload } from "@modules/eirSalesFacade/interfaces/payloads/IEirSalesSavePrequalPayload";
import { mapEirSalesSavePrequalPayload } from "@modules/eirSalesFacade/mappers/mapEirSalesSavePrequalPayload";
import { postSavePrequal } from "@modules/eirSalesFacade/apis/savePrequal";
import { useForm } from "react-hook-form";
import { IEirSalesPrequal } from "@modules/eirSalesFacade/interfaces/models/IEirSalesPrequal";
import { mapEirSalesPrequalWithError } from "@modules/eirSalesFacade/mappers/mapEirSalesPrequalWithError";
import { EDataLayerPrequalResponse, sendPrequalEvent } from "@features/dataLayer/prequal";

interface IParams {
    setPrequal(prequal: IEirSalesPrequal): void;
}

export const usePrequal = (props: IParams) => {
    const { setPrequal } = props;
    const [prequalAddress, setPrequalAddress] = useState<IAddress>();
    const formMethods = useForm<IUserAddress>({ defaultValues: { code: "" } });
    const { startFetching, endFetching, error, isLoading } = useFetchState();
    const prospectReference = getProspectReference();

    const getPrequalAddress = useCallback((address: IAddress) => setPrequalAddress(address), []);

    const submitPrequalAddress = useCallback(
        async (prequalAddressToSend?: IAddress) => {
            if (!prospectReference) {
                return;
            }

            const isEircodeValid = await formMethods.trigger("code");

            if (isEircodeValid && prequalAddressToSend) {
                try {
                    startFetching();

                    const payload: IEirSalesSavePrequalPayload = mapEirSalesSavePrequalPayload(prequalAddressToSend);
                    const savedPrequal = await postSavePrequal(prospectReference, payload);

                    if (savedPrequal) {
                        setPrequal(savedPrequal);
                        sendPrequalEvent(
                            savedPrequal.hasBroadbandService
                                ? EDataLayerPrequalResponse.SUCCESS
                                : EDataLayerPrequalResponse.NOT_AVAILABLE
                        );
                    }

                    endFetching();
                } catch (apiError) {
                    sendPrequalEvent(EDataLayerPrequalResponse.FAILURE);
                    if (apiError.status !== 500) {
                        const prequalWithError: IEirSalesPrequal = mapEirSalesPrequalWithError(
                            prequalAddressToSend,
                            apiError
                        );

                        setPrequal(prequalWithError);

                        endFetching();
                    } else {
                        endFetching(apiError);
                    }
                }
            }
        },
        [endFetching, formMethods, prospectReference, setPrequal, startFetching]
    );

    return {
        formMethods,
        error,
        isLoading,
        prequalAddress,
        getPrequalAddress,
        submitPrequalAddress,
    };
};
